import type { RecordType, SurveyDatasets } from '@factorialco/f0-react'
import { useDataSource } from '@factorialco/f0-react'
import type { PurchaseRequestVendorSelectorQuery } from 'generated/resources/graphql'
import {
  PurchaseRequestCurrencySelectorDocument,
  PurchaseRequestVendorSelectorDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import {
  createGraphqlDataSourceDefinition,
  useGraphqlDataSource,
} from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

function useCurrencyDataSource() {
  const { data } = useQuery(PurchaseRequestCurrencySelectorDocument, {
    variables: { perPage: 200 },
    fetchPolicy: 'cache-first',
  })

  const allCurrencies: RecordType[] =
    data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) ?? []

  return useDataSource(
    {
      search: { enabled: true },
      dataAdapter: {
        fetchData: ({ search }) => {
          const lower = search?.toLowerCase() ?? ''
          const records = lower
            ? allCurrencies.filter((c) => String(c.isoCode).toLowerCase().includes(lower))
            : allCurrencies
          return { records, total: records.length }
        },
      },
    },
    [allCurrencies]
  )
}

const createVendorSourceDefinition = (companyId: string) =>
  createGraphqlDataSourceDefinition({
    document: PurchaseRequestVendorSelectorDocument,
    paginationType: 'infinite-scroll',
    perPage: 20,
    search: { enabled: true },
    mapVariables: ({ search }) => ({ companyId, search: search || undefined }),
    mapData: (data: PurchaseRequestVendorSelectorQuery): RecordType[] =>
      data.finance.contactsConnection.edges.map((edge) => edge.node),
    mapPagination: (data: PurchaseRequestVendorSelectorQuery) => ({
      total: data.finance.contactsConnection.totalCount ?? 0,
      cursor: data.finance.contactsConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.finance.contactsConnection.pageInfo.hasNextPage,
    }),
  })

export function usePurchaseRequestDatasets(companyId: string): SurveyDatasets {
  const currencyDataSource = useCurrencyDataSource()
  const vendorDataSource = useGraphqlDataSource(createVendorSourceDefinition(companyId), [
    companyId,
  ])

  return {
    currency: {
      title: i18n.t('finance.currency'),
      dataSource: currencyDataSource,
      mapOptions: (item) => ({
        value: String(item.isoCode),
        label: String(item.isoCode),
      }),
    },
    vendor: {
      title: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.vendor'
      ),
      dataSource: vendorDataSource,
      mapOptions: (item) => ({
        value: String(item.id),
        label: String(item.legalName || item.name),
      }),
    },
  }
}
